import React, { useEffect, useMemo, useState } from 'react';
import { API_CONFIG, API_URLS } from '../../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import * as Icons from 'lucide-react';
import DOMPurify from 'dompurify';

const DEFAULT_CONTENT = {
  hero: {
    badgeText: 'İletişim',
    badgeIcon: 'MessageSquare',
    title: 'Hadi Konuşalım',
    description:
      'Uzman ekibimiz projenizi dinlemeye hazır. Aşağıdaki formu doldurun, en geç 1 iş günü içinde dönüş yapalım.',
    button1: { text: 'İletişime Geç', link: '/iletisim', icon: 'Phone' }
  },
  contact_cards: [
    {
      icon: 'Mail',
      title: 'E-posta',
      description: 'destek@website-al.com'
    },
    {
      icon: 'Phone',
      title: 'Telefon',
      description: '+90 532 440 40 11'
    },
    {
      icon: 'MapPin',
      title: 'Adres',
      description: 'İstanbul, Türkiye'
    }
  ],
  social_links: [
    { icon: 'Facebook', url: '#' },
    { icon: 'Twitter', url: '#' },
    { icon: 'Linkedin', url: '#' }
  ],
  form: {
    title: 'Bize Yazın',
    success_badge: 'Gönderildi',
    submit_button: { text: 'Gönder', icon: 'Send', loading_text: 'Gönderiliyor...' }
  },
  map: {
    enabled: true,
    embed_html: null
  }
};

const resolveIcon = (name, fallback = 'Circle') => {
  if (name && Icons[name]) {
    return Icons[name];
  }
  return Icons[fallback] || Icons.Circle;
};

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '+90 ', subject: '', message: '' });
  const [settings, setSettings] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [contentReady, setContentReady] = useState(false);

  const apiBase = useMemo(
    () => (process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : ''),
    []
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);

    const loadSettings = async () => {
      try {
        const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getCompanySettings`);
        const json = await res.json();
        if (json.status === 'success') setSettings(json.data || {});
      } catch (error) {
        console.error('Şirket ayarları yüklenemedi:', error);
      }
    };
    loadSettings();

    const loadContent = async () => {
      try {
        const res = await fetch(`${apiBase}/backend/admin_api.php?action=getPageContent&slug=iletisim`);
        const data = await res.json();
        if (data.status === 'success' && data.data) {
          // Parse additional_sections if it's a string (backend might already parse it)
          const rawAdditional = data.data.additional_sections;
          let additional = {};
          
          if (rawAdditional) {
            if (typeof rawAdditional === 'string') {
              try {
                additional = JSON.parse(rawAdditional);
              } catch (err) {
                console.error('İletişim additional_sections parse error:', err);
                additional = {};
              }
            } else if (typeof rawAdditional === 'object') {
              additional = rawAdditional;
            }
          }
          
          setPageContent({
            ...data.data,
            additional_sections: additional
          });
        }
      } catch (error) {
        console.error('İletişim içeriği yüklenemedi:', error);
      } finally {
        setLoading(false);
      }
    };
    loadContent();

    return () => clearTimeout(timer);
  }, [apiBase]);

  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace('+90 ', '').replace(/\D/g, '');
    const limited = phoneNumber.slice(0, 10);

    let formatted = '+90 ';
    if (limited.length > 0) formatted += limited.slice(0, 3);
    if (limited.length > 3) formatted += ' ' + limited.slice(3, 6);
    if (limited.length > 6) formatted += ' ' + limited.slice(6, 8);
    if (limited.length > 8) formatted += ' ' + limited.slice(8, 10);

    return formatted;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setForm({ ...form, phone: formatted });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      setSending(true);
      const payload = {
        service_id: 'contact',
        service_title: `İletişim Formu: ${form.subject || 'Konu yok'}`,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone || null,
        company_name: null,
        project_description: form.message,
        budget_range: null,
        timeline: null,
        additional_requirements: null
      };
      const res = await fetch(`${API_CONFIG.BASE_URL}/backend/api.php?action=createQuoteRequest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        throw new Error('Form gönderilemedi');
      }
      setSent(true);
      setForm({ name: '', email: '', phone: '+90 ', subject: '', message: '' });
    } finally {
      setSending(false);
    }
  };

  const merged = useMemo(() => {
    const mapSection = pageContent?.additional_sections?.map || {};
    const mapConfig = {
      ...DEFAULT_CONTENT.map,
      ...mapSection,
      enabled: mapSection.enabled !== undefined ? mapSection.enabled : DEFAULT_CONTENT.map.enabled,
      embed_html: mapSection.embed_html || null
    };
    
    return {
      hero: {
        ...DEFAULT_CONTENT.hero,
        ...(pageContent || {})
      },
      contact_cards: (
        pageContent?.additional_sections?.contact_cards?.length
          ? pageContent.additional_sections.contact_cards
          : DEFAULT_CONTENT.contact_cards
      ).map((card, idx) => ({
        ...card,
        key: `${card.title || 'card'}-${idx}`
      })),
      social_links: (pageContent?.additional_sections?.social_links ||
        DEFAULT_CONTENT.social_links).map((link, idx) => ({
        ...link,
        key: `${link.icon || 'social'}-${idx}`
      })),
      form: {
        ...DEFAULT_CONTENT.form,
        ...(pageContent?.additional_sections?.form || {})
      },
      map: mapConfig
    };
  }, [pageContent]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#7A5AFF]" />
      </div>
    );
  }

  const HeroBadgeIcon = resolveIcon(merged.hero.hero_badge_icon || merged.hero.badgeIcon, 'MessageSquare');

  return (
    <div className="min-h-screen bg-[#f5f3ff]/30">
      <section
        className="bg-gradient-to-br from-black via-[#0f0a1f] to-[#1a0a2e] py-20 text-white"
        style={{
          opacity: contentReady ? 1 : 0,
          transition: 'opacity 0.6s ease-in'
        }}
      >
        <div className="container mx-auto px-6 text-center">
          <div className="mb-6 inline-flex items-center space-x-3 rounded-full bg-[#7A5AFF]/20 px-6 py-3 backdrop-blur">
            <HeroBadgeIcon className="h-5 w-5 text-[#7A5AFF]" />
            <span>{merged.hero.hero_badge_text || merged.hero.badgeText}</span>
          </div>
          <h1 className="mb-4 text-4xl font-bold md:text-6xl">
            {merged.hero.hero_title || merged.hero.title}
          </h1>
          <p className="mx-auto max-w-2xl text-white/85">
            {merged.hero.hero_description || merged.hero.description}
          </p>
        </div>
      </section>

      <div
        className="container mx-auto grid grid-cols-1 gap-8 px-6 py-16 lg:grid-cols-3"
        style={{
          opacity: contentReady ? 1 : 0,
          transition: 'opacity 0.6s ease-in'
        }}
      >
        <div className="space-y-6">
          <div className="space-y-6">
            {merged.contact_cards.map((card) => {
              const Icon = resolveIcon(card.icon, 'Info');
              let description = card.description;
              if (!description) {
                if (card.icon === 'Mail') description = settings?.email || DEFAULT_CONTENT.contact_cards[0].description;
                if (card.icon === 'Phone') description = settings?.phone || DEFAULT_CONTENT.contact_cards[1].description;
                if (card.icon === 'MapPin')
                  description = settings?.address || DEFAULT_CONTENT.contact_cards[2].description;
              }
              
              // URL yapısını belirle
              let cardUrl = card.url || null;
              if (!cardUrl && description) {
                if (card.icon === 'Mail') {
                  cardUrl = `mailto:${description}`;
                } else if (card.icon === 'Phone') {
                  cardUrl = `tel:${description.replace(/\s/g, '')}`;
                } else if (card.icon === 'MapPin') {
                  // Önce settings'ten directions_url'i çek, yoksa Google Maps
                  cardUrl = settings?.directions_url || 
                            `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(description)}`;
                }
              }
              
              const CardWrapper = cardUrl ? 'a' : 'div';
              const cardProps = cardUrl ? {
                href: cardUrl,
                target: card.icon === 'MapPin' ? '_blank' : '_self',
                rel: card.icon === 'MapPin' ? 'noopener noreferrer' : undefined,
                className: 'block hover:shadow-xl transition-shadow cursor-pointer'
              } : {};
              
              // Telefon numarasını WhatsApp için hazırla
              const phoneNumber = card.icon === 'Phone' ? (description || settings?.phone || DEFAULT_CONTENT.contact_cards[1].description) : null;
              const whatsappNumber = phoneNumber ? phoneNumber.replace(/\s/g, '').replace('+', '') : null;
              const whatsappUrl = whatsappNumber ? `https://wa.me/${whatsappNumber}` : null;
              
              return (
                <React.Fragment key={card.key}>
                  <Card className="shadow-lg">
                    <CardWrapper {...cardProps}>
                      <CardContent className="flex items-start gap-4 p-6">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-[#7A5AFF] to-[#5a3fd9] text-white">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{card.title}</h3>
                          <p className="whitespace-pre-line text-gray-600">{description}</p>
                        </div>
                      </CardContent>
                    </CardWrapper>
                  </Card>
                  
                  {/* WhatsApp kartı telefon kartının hemen altında */}
                  {card.icon === 'Phone' && whatsappUrl && (
                    <Card className="shadow-lg">
                      <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block hover:shadow-xl transition-shadow cursor-pointer"
                      >
                        <CardContent className="flex items-start gap-4 p-6">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-green-500 to-green-600 text-white">
                            <svg
                              className="h-5 w-5"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">WhatsApp</h3>
                            <p className="whitespace-pre-line text-gray-600">{phoneNumber}</p>
                          </div>
                        </CardContent>
                      </a>
                    </Card>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <Card className="shadow-lg">
            <CardContent className="p-6">
              <h3 className="mb-3 font-semibold text-gray-900">
                {pageContent?.additional_sections?.social_title || 'Sosyal Medya'}
              </h3>
              <div className="flex flex-wrap items-center gap-3">
                {merged.social_links.map((link) => {
                  const Icon = resolveIcon(link.icon, 'Share2');
                  return (
                    <a
                      key={link.key}
                      href={link.url || '#'}
                      target={link.url ? '_blank' : '_self'}
                      rel="noreferrer"
                      className="rounded bg-[#7A5AFF]/10 p-2 transition hover:bg-[#7A5AFF]/20 text-[#7A5AFF]"
                    >
                      <Icon className="h-5 w-5" />
                    </a>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{merged.form.title || DEFAULT_CONTENT.form.title}</span>
                {sent && (
                  <Badge className="bg-green-100 text-green-700">
                    {merged.form.success_badge || DEFAULT_CONTENT.form.success_badge}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-6">
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Ad Soyad</label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Adınız Soyadınız"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">E-posta</label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="ornek@email.com"
                      pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                      title="Geçerli bir e-posta adresi girin"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Telefon</label>
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={handlePhoneChange}
                      placeholder="+90 555 123 45 67"
                      title="Telefon numaranızı girin"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Konu</label>
                  <Input
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="Konuyu yazın"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Mesaj</label>
                  <textarea
                    rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7A5AFF] focus:border-[#7A5AFF]"
                    placeholder="Mesajınızı yazın"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={sending}
                    className={`${sending ? 'bg-[#7A5AFF]/60 cursor-not-allowed' : 'bg-[#7A5AFF] hover:bg-[#8b6bff]'} text-white shadow-lg shadow-[#7A5AFF]/30`}
                  >
                    {(() => {
                      const SubmitIcon = resolveIcon(
                        merged.form.submit_button?.icon || DEFAULT_CONTENT.form.submit_button.icon,
                        'Send'
                      );
                      return (
                        <>
                          <SubmitIcon className="mr-2 h-4 w-4" />
                          {sending
                            ? merged.form.submit_button?.loading_text || DEFAULT_CONTENT.form.submit_button.loading_text
                            : merged.form.submit_button?.text || DEFAULT_CONTENT.form.submit_button.text}
                        </>
                      );
                    })()}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      <div
        className="container mx-auto px-6 pb-16"
        style={{
          opacity: contentReady ? 1 : 0,
          transition: 'opacity 0.6s ease-in'
        }}
      >
        <Card className="shadow-lg">
          <CardContent className="p-0">
            {(() => {
              const shouldShowMap = merged.map.enabled && merged.map.embed_html && merged.map.embed_html.trim();
              
              if (shouldShowMap) {
                // Admin panelinden gelen embed HTML güvenilir olduğu için DOMPurify kullanmıyoruz
                return (
                  <div
                    className="w-full overflow-hidden rounded-b-2xl [&_iframe]:w-full [&_iframe]:h-[450px]"
                    dangerouslySetInnerHTML={{ __html: merged.map.embed_html }}
                  />
                );
              }
              
              if (merged.map.enabled && settings?.address) {
                return (
                  <div className="w-full overflow-hidden rounded-b-2xl" style={{ height: '450px' }}>
                    <iframe
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6d-s6U4Zb3O-rQ&q=${encodeURIComponent(settings.address)}`}
                    />
                  </div>
                );
              }
              
              return (
                <div className="flex h-64 w-full items-center justify-center bg-gray-200 text-gray-500">
                  Harita (yakında)
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContactPage;
